import { useCallback, useRef } from "react"

export type MeetingAudioGraph = {
  context: AudioContext
  source: MediaStreamAudioSourceNode
}

// Creates a single Web Audio graph rooted in the given audio stream. Other hooks (capture, ducking, etc.)
// connect their own nodes to graph.source instead of creating a separate AudioContext for each one.
const useMeetingAudioGraph = () => {
  const graphRef = useRef<MeetingAudioGraph | null>(null)

  const setupGraph = useCallback(
    async (stream: MediaStream): Promise<MeetingAudioGraph> => {
      const context = new AudioContext()
      await context.resume() // required for iOS Safari
      const source = context.createMediaStreamSource(stream)
      graphRef.current = { context, source }
      return { context, source }
    },
    []
  )

  const teardownGraph = useCallback(() => {
    graphRef.current?.source.disconnect()
    graphRef.current?.context.close()
    graphRef.current = null
  }, [])

  return { setupGraph, teardownGraph }
}

export default useMeetingAudioGraph
